<template>
  <div>
    <!-- Filters Component-->
    <StockCompareFilters
      @filter-changed="handleFilterChange"
      @filters-reset="handleFiltersReset"
    />

    <!-- Metrics Table -->
    <StockCompareMetricsTable />

    <!-- Chart Sections -->
    <ChartSection
      v-for="section in chartSections"
      :key="section.key"
      :title="section.title"
      :primary-data="section.primaryData"
      :competitor-data="section.competitorData"
      :colors="section.colors"
      :total-label="section.totalLabel"
      :single-column="section.singleColumn"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { createChartSections } from '../helpers/createChartSections'
import { mockData } from '../mockData/stockCompareChartsMockData'
import { getMockDataForMake } from '../helpers/mockDataHelper'

export default {
  name: 'StockCompareDashboardCharts',
  components: {
    StockCompareMetricsTable: () => import('./StockCompareMetricsTable'),
    StockCompareFilters: () => import('./StockCompareFilters'),
    ChartSection: () => import('./StockCompareChartSection')
  },

  computed: {
    ...mapGetters('stockcompare/dashboard', [
      'primaryDaysInStockBreakdown',
      'competitorDaysInStockBreakdown',
      'primaryPercentBreakdown',
      'competitorPercentBreakdown',
      'primaryMarketDemandBreakdown',
      'competitorMarketDemandBreakdown',
      'primaryModelBreakdown',
      'competitorModelBreakdown',
      'primaryDealerTypeBreakdown',
      'competitorDealerTypeBreakdown',
      'primaryNearlyNewBreakdown',
      'competitorNearlyNewBreakdown',
      'myMakes',
      'competitorMakes'
    ]),

    chartSections() {
      return createChartSections({
        primaryDaysInStockData: this.primaryDaysInStockData,
        competitorDaysInStockData: this.competitorDaysInStockData,
        primaryPercentData: this.primaryPercentData,
        competitorPercentData: this.competitorPercentData,
        primaryMarketDemandData: this.primaryMarketDemandData,
        competitorMarketDemandData: this.competitorMarketDemandData,
        primaryModelData: this.primaryModelData,
        competitorModelData: this.competitorModelData,
        primaryDealerTypeData: this.primaryDealerTypeData,
        competitorDealerTypeData: this.competitorDealerTypeData,
        primaryNearlyNewData: this.primaryNearlyNewData,
        competitorNearlyNewData: this.competitorNearlyNewData,
        primaryMileageData: this.primaryMileageData,
        competitorMileageData: this.competitorMileageData
      })
    },

    // Days in stock have a special format.
    getDaysInStockData() {
      return (breakdown, makes, isCompetitor = false) => {
        if (!makes || makes.length === 0) {
          return null
        }

        const ranges = [
          '0-9',
          '10-19',
          '20-29',
          '30-39',
          '40-49',
          '50-59',
          '60-69',
          '70-79',
          '80-89',
          '90+'
        ]
        const labels = [
          '00-09 days',
          '10-19 days',
          '20-29 days',
          '30-39 days',
          '40-49 days',
          '50-59 days',
          '60-69 days',
          '70-79 days',
          '80-89 days',
          '90+ days'
        ]

        if (breakdown) {
          return {
            series: ranges.map(r => breakdown[r] || 0),
            labels
          }
        }

        // Get mock data based on selected make
        return this.getMockDataForMake(makes, 'daysInStock')
      }
    },

    // Get rest of the data
    getSeriesData() {
      return (
        breakdown,
        makes,
        formatPercent = false,
        isCompetitor = false
      ) => {
        if (!makes || makes.length === 0) {
          return null
        }

        if (breakdown) {
          return {
            series: Object.values(breakdown),
            labels: Object.keys(breakdown).map(key =>
              formatPercent ? `${key}%` : key
            )
          }
        }

        // Get mock data based on selected make
        return this.getMockDataForMake(
          makes,
          formatPercent ? 'percent' : 'defaultType'
        )
      }
    },

    primaryDaysInStockData() {
      return this.getDaysInStockData(
        this.primaryDaysInStockBreakdown,
        this.myMakes,
        false
      )
    },

    competitorDaysInStockData() {
      return this.getDaysInStockData(
        this.competitorDaysInStockBreakdown,
        this.competitorMakes,
        true
      )
    },

    primaryDealerTypeData() {
      return (
        this.getSeriesData(
          this.primaryDealerTypeBreakdown,
          this.myMakes,
          false,
          false
        ) || this.getMockDataForMake(this.myMakes, 'dealerType')
      )
    },

    competitorDealerTypeData() {
      return (
        this.getSeriesData(
          this.competitorDealerTypeBreakdown,
          this.competitorMakes,
          false,
          true
        ) || this.getMockDataForMake(this.competitorMakes, 'dealerType')
      )
    },

    primaryNearlyNewData() {
      return (
        this.getSeriesData(
          this.primaryNearlyNewBreakdown,
          this.myMakes,
          false,
          false
        ) || this.getMockDataForMake(this.myMakes, 'nearlyNew')
      )
    },

    competitorNearlyNewData() {
      return (
        this.getSeriesData(
          this.competitorNearlyNewBreakdown,
          this.competitorMakes,
          false,
          true
        ) || this.getMockDataForMake(this.competitorMakes, 'nearlyNew')
      )
    },

    primaryPercentData() {
      return (
        this.getSeriesData(
          this.primaryPercentBreakdown,
          this.myMakes,
          true,
          false
        ) || this.getMockDataForMake(this.myMakes, 'percent')
      )
    },

    competitorPercentData() {
      return (
        this.getSeriesData(
          this.competitorPercentBreakdown,
          this.competitorMakes,
          true,
          true
        ) || this.getMockDataForMake(this.competitorMakes, 'percent')
      )
    },

    primaryMarketDemandData() {
      return (
        this.getSeriesData(
          this.primaryMarketDemandBreakdown,
          this.myMakes,
          false,
          false
        ) || this.getMockDataForMake(this.myMakes, 'marketDemand')
      )
    },

    competitorMarketDemandData() {
      return (
        this.getSeriesData(
          this.competitorMarketDemandBreakdown,
          this.competitorMakes,
          false,
          true
        ) || this.getMockDataForMake(this.competitorMakes, 'marketDemand')
      )
    },

    primaryModelData() {
      // Special handling for model data - combine all models from all selected makes
      if (!this.myMakes || this.myMakes.length === 0) {
        return null
      }

      if (this.primaryModelBreakdown) {
        return {
          series: Object.values(this.primaryModelBreakdown),
          labels: Object.keys(this.primaryModelBreakdown)
        }
      }

      // Get model data for all selected makes and combine them (concatenate, not sum)
      return this.getCombinedModelData(this.myMakes)
    },

    competitorModelData() {
      // Special handling for model data - combine all models from all selected makes
      if (!this.competitorMakes || this.competitorMakes.length === 0) {
        return null
      }

      if (this.competitorModelBreakdown) {
        return {
          series: Object.values(this.competitorModelBreakdown),
          labels: Object.keys(this.competitorModelBreakdown)
        }
      }

      // Get model data for all selected makes and combine them (concatenate, not sum)
      return this.getCombinedModelData(this.competitorMakes)
    },

    primaryMileageData() {
      if (!this.myMakes || this.myMakes.length === 0) {
        return null
      }
      return this.getMockDataForMake(this.myMakes, 'mileage')
    },

    competitorMileageData() {
      if (!this.competitorMakes || this.competitorMakes.length === 0) {
        return null
      }
      return this.getMockDataForMake(this.competitorMakes, 'mileage')
    }
  },

  methods: {
    getMockDataForMake(makes, dataType) {
      return getMockDataForMake(makes, dataType, mockData)
    },

    getCombinedModelData(makes) {
      let allModels = []
      let allSeries = []

      makes.forEach(make => {
        const makeData = this.getMockDataForMake([make], 'model')
        if (makeData && makeData.labels && makeData.series) {
          allModels.push(...makeData.labels)
          allSeries.push(...makeData.series)
        }
      })

      return {
        labels: allModels,
        series: allSeries
      }
    },

    handleFilterChange(filterData) {
      this.$emit('filters-updated', filterData)
    },

    handleFiltersReset(filters) {
      this.$emit('filters-reset', filters)
    }
  }
}
</script>
